import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import { IconSearch } from "@tabler/icons-react"
import type React from "react"
import SearchLogFilterForm, { type SearchLogFilterValues } from "./components/SearchLogFilterForm"
import SearchLogTable from "./components/SearchLogTable"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"
import { logService } from "@/services/logApi"

export interface SearchLogQueryParams {
  page: number
  per_page: number
  search?: string
  match_type?: string
  commit_method?: string
}

export const SearchLogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<SearchLogQueryParams>({
    ...PAGE_CONFIG,
    search: "",
    match_type: undefined,
    commit_method: undefined,
  })

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["search_logs_list", searchParams],
    queryFn: () =>
      logService.listSearchLogs({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search ? searchParams.search.trim() : undefined,
        match_type: searchParams.match_type || undefined,
        commit_method: searchParams.commit_method || undefined,
      }),
  })

  const handleSearch = (values: SearchLogFilterValues) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
    })
  }

  const items = data?.data || []
  const totalCount = data?.total ?? 0

  return (
    <div className="flex flex-col gap-2">
      <SearchLogFilterForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          match_type: searchParams.match_type,
          commit_method: searchParams.commit_method,
        }}
      />

      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <IconSearch className="text-blue-500" size={20} />
              <span className="font-semibold text-base text-gray-800">Log Tìm Kiếm (Search Logs)</span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số lượt tìm: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} loading={isFetching}>
              Làm mới
            </Button>
          </div>
        }
      >
        <SearchLogTable
          data={items}
          loading={isFetching}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>
    </div>
  )
}

export default SearchLogPage
